import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminStats } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const Stat = ({ label, value, color = '#fff', sub }) => {
  // Split value to handle USDT separately
  const isUSDT = typeof value === 'string' && value.includes('USDT');
  const parts = isUSDT ? value.split(' ') : [value];

  return (
    <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>
        {parts[0]}
        {isUSDT && <span style={{ fontSize: 14, marginLeft: 4, color: '#666' }}>{parts[1]}</span>}
      </div>
      {sub && <div style={{ color: '#444', fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getAdminStats()
      .then((res) => setStats(res.data.stats))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#555', fontSize: 13 }}>Loading...</div>;
  if (!stats) return <div style={{ color: '#ff6666', fontSize: 13 }}>Failed to load stats.</div>;

  const wt = stats.walletTotals || {};

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Dashboard</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Platform overview</div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <Stat label="Total Users"       value={stats.totalUsers}   color={cyan} sub={`${stats.activeUsers} active`} />
        <Stat label="Slots Purchased"   value={stats.totalSlots}   color="#cc66ff" sub={`${stats.totalSlotAmount.toFixed(2)} USDT total`} />
        <Stat label="Total Deposits"    value={`${stats.totalDeposits.toFixed(2)} USDT`} color="#66ff99" />
        <Stat label="Total P2P Transfers" value={`${stats.totalP2P.toFixed(2)} USDT`} color="#ffaa44" />
        <Stat label="Total Revenue"     value={`${stats.totalRevenue.toFixed(2)} USDT`} color="#00ff88" sub="Withdrawal charges (10%)" />
        <Stat label="Pending Withdrawals" value={stats.pendingWithdrawals} color="#ffaa44" />
        <Stat label="Total Withdrawn"   value={`${stats.totalWithdrawn.toFixed(2)} USDT`} color="#ff6666" />
        <Stat label="External Withdrawals" value={`${stats.totalExternalWithdraw.toFixed(2)} USDT`} color="#00ddff" />
        <Stat label="Main Wallet Pool"  value={`${(wt.main_wallet || 0).toFixed(2)} USDT`}    color="#66aaff" />
        <Stat label="Earning Wallet Pool" value={`${(wt.earning_wallet || 0).toFixed(2)} USDT`} color="#66ff99" />
        <Stat label="One Wallet Pool"   value={`${(wt.one_wallet || 0).toFixed(2)} USDT`}     color="#ff88cc" />
        <Stat label="Commission Jobs"   value={stats.pendingJobs}  color="#ffaa44" sub={`${stats.failedJobs} failed`} />
      </div>

      {/* Recent Users */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600 }}>Recent Registrations</div>
          <button onClick={() => navigate('/panel/users')} style={{ background: 'none', border: 'none', color: cyan, fontSize: 12, cursor: 'pointer' }}>
            View all →
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
              {['Name', 'Email', 'Code', 'Joined', 'Status'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '8px 12px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.recentUsers.map((u) => (
              <tr key={u._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer' }}
                onClick={() => navigate(`/panel/users/${u._id}`)}>
                <td style={{ color: '#fff', fontSize: 13, padding: '10px 12px' }}>{u.name}</td>
                <td style={{ color: '#888', fontSize: 12, padding: '10px 12px' }}>{u.email}</td>
                <td style={{ color: cyan, fontSize: 12, padding: '10px 12px', fontFamily: 'monospace' }}>{u.userCode}</td>
                <td style={{ color: '#555', fontSize: 12, padding: '10px 12px' }}>{fmtIST(u.createdAt)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: u.isActive ? 'rgba(102,255,153,0.12)' : 'rgba(200,50,50,0.12)', color: u.isActive ? '#66ff99' : '#ff6666', border: `1px solid ${u.isActive ? 'rgba(102,255,153,0.3)' : 'rgba(200,50,50,0.3)'}`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
